
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    Grid,
    Tab,
    Tabs,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    Paper,
    InputAdornment,
    Avatar,
    Chip,
    Switch,
    Card,
    IconButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Add as AddIcon,
    Extension as IntegrationIcon,
    Extension as ExtensionIcon,
    Search as SearchIcon,
    Delete,
    Add,
    LockSharp as LockIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import IntegrationCard from '../../components/IntegrationCard';
import CodeEditor from '../../components/CodeEditor';
import * as integrationService from '../../services/integrationService';
import * as toolService from '../../services/toolService';
import { useAuth } from '../../hooks/useAuth';

const IntegrationsPage = () => {
    const { user } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [pageData, setPageData] = useState(null);
    const [integrations, setIntegrations] = useState([]);
    const [tools, setTools] = useState([]);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [loadingPage, setLoadingPage] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);
    const [loadingDialog, setLoadingDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        tool_id: '',
        description: '',
        config: {}
    });

    // Draft state for dynamic fields (to keep in-progress rows like empty new items)
    const [dynamicDrafts, setDynamicDrafts] = useState({});

    // Use memoized filtered integrations
    const filteredIntegrations = useMemo(() => {
        let filtered = integrations;

        // Filter by tab
        if (tabValue === 1) {
            filtered = filtered.filter(integration => integration.status === 'active');
        } else if (tabValue === 2) {
            filtered = filtered.filter(integration => integration.status !== 'active');
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(integration =>
                integration.name.toLowerCase().includes(query) ||
                integration.description?.toLowerCase().includes(query) ||
                integration.Tool?.name.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [integrations, tabValue, searchQuery]);

    // Fetch page data
    const fetchPageData = useCallback(async () => {
        setLoadingPage(true);
        setError(null);
        try {
            const [integrationsData, toolsData] = await Promise.all([
                integrationService.getUserIntegrations(),
                toolService.getAllTools()
            ]);

            setIntegrations(integrationsData || []);
            setTools(toolsData || []);
        } catch (err) {
            console.error('Error fetching integrations page data:', err);
            setError('Failed to load integrations data');
            setSnackbar({
                open: true,
                message: 'Failed to load integrations data',
                severity: 'error'
            });
        } finally {
            setLoadingPage(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    // Log formData changes for debugging
    useEffect(() => {
        if (dialogMode === 'edit' && selectedIntegration) {
            if (process.env.NODE_ENV !== 'production') console.log('Form data updated:', formData);
        }
    }, [formData, dialogMode, selectedIntegration]);

    // Handle tab change
    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
    }, []);

    // Handle dialog open for create mode
    const handleDialogOpen = useCallback(() => {
        setDialogMode('create');
        setSelectedIntegration(null);
        setFormData({
            name: '',
            tool_id: '',
            description: '',
            config: {}
        });
        setDynamicDrafts({});
        setOpenDialog(true);
    }, []);

    // Handle dialog close
    const handleDialogClose = useCallback(() => {
        setOpenDialog(false);
        setLoadingDialog(false);
        setDynamicDrafts({});
    }, []);

    // Handle dialog open for edit mode
    const handleEditDialogOpen = useCallback((integration) => {
        if (integration) {
            setDialogMode('edit');
            setSelectedIntegration(integration);

            if (process.env.NODE_ENV !== 'production') console.log('Opening integration for edit:', integration);
            if (process.env.NODE_ENV !== 'production') console.log('Integration config:', integration.config);

            // Debug the structure
            if (integration.config) {
                Object.keys(integration.config).forEach(key => {
                    if (process.env.NODE_ENV !== 'production') console.log(`Config[${key}]:`, integration.config[key]);
                    if (typeof integration.config[key] === 'object') {
                        Object.keys(integration.config[key]).forEach(subKey => {
                            if (process.env.NODE_ENV !== 'production') console.log(`  Config[${key}][${subKey}]:`, integration.config[key][subKey]);
                        });
                    }
                });
            }

            // Deep clone to avoid mutating the original integration object while editing (edits should only persist on Save)
            const deepClone = (obj) => {
                try {
                    return obj ? JSON.parse(JSON.stringify(obj)) : obj;
                } catch (e) {
                    if (process.env.NODE_ENV !== 'production') console.warn('Failed to deep clone integration.config, falling back to shallow copy', e);
                    return obj ? { ...obj } : obj;
                }
            };

            // Set form data with integration data (using a detached copy of config)
            setFormData({
                name: integration.name,
                tool_id: integration.tool_id,
                description: integration.description || '',
                config: deepClone(integration.config) || {}
            });

            setDynamicDrafts({});
            setOpenDialog(true);
        }
    }, []);

    // Handle delete dialog open
    const handleDeleteDialogOpen = useCallback((integration) => {
        setSelectedIntegration(integration);
        setOpenDeleteDialog(true);
    }, []);

    // Handle delete dialog close
    const handleDeleteDialogClose = useCallback(() => {
        setOpenDeleteDialog(false);
    }, []);

    // Handle form input change
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    // Handle config field change
    const handleConfigChange = useCallback((key, value) => {
        setFormData(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [key]: value
            }
        }));
    }, []);

    // Handle form submit
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoadingDialog(true);

        try {
            if (dialogMode === 'create') {
                // Create new integration
                const newIntegration = await integrationService.createIntegration(formData);

                setIntegrations(prev => [...prev, newIntegration]);
                setSnackbar({
                    open: true,
                    message: 'Integration created successfully',
                    severity: 'success'
                });
            } else {
                // Update existing integration
                const updatedIntegration = await integrationService.updateIntegration(
                    selectedIntegration.id,
                    formData
                );

                setIntegrations(prev =>
                    prev.map(item => item.id === updatedIntegration.id ? updatedIntegration : item)
                );
                setSnackbar({
                    open: true,
                    message: 'Integration updated successfully',
                    severity: 'success'
                });
            }

            handleDialogClose();
        } catch (err) {
            console.error('Error saving integration:', err);
            setSnackbar({
                open: true,
                message: `Failed to ${dialogMode === 'create' ? 'create' : 'update'} integration`,
                severity: 'error'
            });
        } finally {
            setLoadingDialog(false);
        }
    }, [dialogMode, formData, selectedIntegration, handleDialogClose]);

    // Handle delete integration
    const handleDeleteIntegration = useCallback(async () => {
        if (!selectedIntegration) return;

        setLoadingAction(true);
        try {
            await integrationService.deleteIntegration(selectedIntegration.id);

            setIntegrations(prev => prev.filter(item => item.id !== selectedIntegration.id));
            setSnackbar({
                open: true,
                message: 'Integration deleted successfully',
                severity: 'success'
            });

            handleDeleteDialogClose();
        } catch (err) {
            console.error('Error deleting integration:', err);
            setSnackbar({
                open: true,
                message: 'Failed to delete integration',
                severity: 'error'
            });
        } finally {
            setLoadingAction(false);
        }
    }, [selectedIntegration, handleDeleteDialogClose]);

    // Handle snackbar close
    const handleSnackbarClose = useCallback(() => {
        setSnackbar(prev => ({
            ...prev,
            open: false
        }));
    }, []);

    // Get selected tool schema (normalized) and root key
    const { selectedTool, normalizedSchema, schemaRootKey } = useMemo(() => {
        if (!formData.tool_id) return { selectedTool: null, normalizedSchema: null, schemaRootKey: null };
        const tool = tools.find(t => t.id === parseInt(formData.tool_id));
        const rawSchema = tool?.schema || null;

        if (!rawSchema) return { selectedTool: tool || null, normalizedSchema: null, schemaRootKey: null };

        // If schema follows the JSON schema-like shape from the example
        if (typeof rawSchema === 'object' && rawSchema.type === 'object' && rawSchema.properties) {
            return {
                selectedTool: tool || null,
                normalizedSchema: rawSchema.properties,
                schemaRootKey: rawSchema.id || null
            };
        }

        // Fallback: assume rawSchema is already a map of fields
        return { selectedTool: tool || null, normalizedSchema: rawSchema, schemaRootKey: null };
    }, [formData.tool_id, tools]);

    // Clear dynamic drafts when tool changes
    useEffect(() => {
        setDynamicDrafts({});
    }, [formData.tool_id]);

    // Helpers to get/set config values respecting schemaRootKey
    const getConfigValue = useCallback((key, fallback = '') => {
        const cfg = formData.config && typeof formData.config === 'object' ? formData.config : {};

        if (process.env.NODE_ENV !== 'production') console.log('getConfigValue:', key, 'config:', cfg, 'schemaRootKey:', schemaRootKey);

        // Special handling for custom code with function_identifier structure
        const isCustomCode = (schemaRootKey === 'custom_code') || ((selectedTool?.name || '').toLowerCase().includes('custom code'));

        if (isCustomCode) {
                const customObj = cfg['custom_code'];
                if (customObj) {

                    // If we're looking for function_identifier, check direct access first
                    if (key === 'function_identifier') {
                        // Look through all function identifiers to find one
                        const functionIds = Object.keys(customObj);
                        if (functionIds.length > 0) {
                            const firstFunctionId = functionIds[0];
                            if (customObj[firstFunctionId] && customObj[firstFunctionId].function_identifier) {
                                return customObj[firstFunctionId].function_identifier;
                            }
                            return firstFunctionId; // fallback to key name
                        }
                        return fallback;
                    }

                    // For other fields, look inside function_identifier structures
                    const functionIds = Object.keys(customObj);
                    for (const functionId of functionIds) {
                        const functionObj = customObj[functionId];
                        if (functionObj && typeof functionObj === 'object' && functionObj[key] !== undefined) {
                            return functionObj[key];
                        }
                    }
                }
        }

        // Prefer explicit schema root key
        if (schemaRootKey && cfg) {
            const rootObj = cfg[schemaRootKey];
            if (rootObj) {
                if (Object.prototype.hasOwnProperty.call(rootObj, key)) {
                    return rootObj[key];
                }
                if (typeof rootObj === 'object' && !Array.isArray(rootObj)) {
                    const firstChild = Object.values(rootObj)[0];
                    if (firstChild && typeof firstChild === 'object' && Object.prototype.hasOwnProperty.call(firstChild, key)) {
                        return firstChild[key];
                    }
                }
            }
        }

        // Try to infer a single root key from existing config (legacy patterns like google_sheets)
        const cfgKeys = Object.keys(cfg || {});
        if (!schemaRootKey && cfgKeys.length === 1) {
            const inferredKey = cfgKeys[0];
            const inferredRoot = cfg[inferredKey];
            if (inferredRoot && typeof inferredRoot === 'object') {
                if (Object.prototype.hasOwnProperty.call(inferredRoot, key)) {
                    return inferredRoot[key];
                }
                const firstChild = Object.values(inferredRoot)[0];
                if (firstChild && typeof firstChild === 'object' && Object.prototype.hasOwnProperty.call(firstChild, key)) {
                    return firstChild[key];
                }
            }
        }

        // Fallback to flat config
        return (cfg && cfg[key] !== undefined) ? cfg[key] : fallback;
    }, [formData.config, schemaRootKey, selectedTool]);

    const setConfigValue = useCallback((key, value) => {
        setFormData(prev => {
            const nextConfig = { ...(prev.config || {}) };

            if (process.env.NODE_ENV !== 'production') console.log('setConfigValue:', key, value, 'current config:', nextConfig, 'schemaRootKey:', schemaRootKey);

            // Special handling for custom code with function_identifier structure
            const isCustomCode = (schemaRootKey === 'custom_code') || ((selectedTool?.name || '').toLowerCase().includes('custom code'));

            if (isCustomCode) {
                const customKey = 'custom_code'; // Fix: ensure correct key

                if (!nextConfig[customKey]) {
                    nextConfig[customKey] = {};
                }

                // Get function identifier
                let functionId = 'customFunction'; // default

                if (key === 'function_identifier') {
                    functionId = value || 'customFunction';
                    // If renaming function identifier, move existing object to the new key
                    const existingIds = Object.keys(nextConfig[customKey] || {});
                    if (existingIds.length === 1 && existingIds[0] !== functionId) {
                        const oldId = existingIds[0];
                        nextConfig[customKey][functionId] = { ...(nextConfig[customKey][oldId] || {}) };
                        delete nextConfig[customKey][oldId];
                    }
                } else {
                    // Try to find existing function_identifier
                    const existingFunctionIds = Object.keys(nextConfig[customKey]);
                    if (existingFunctionIds.length > 0) {
                        functionId = existingFunctionIds[0];
                    } else {
                        // Look for function_identifier in form data
                        const formFunctionId = getConfigValue('function_identifier', 'customFunction');
                        functionId = formFunctionId || 'customFunction';
                    }
                }

                // Ensure function object exists
                if (!nextConfig[customKey][functionId]) {
                    nextConfig[customKey][functionId] = {};
                }

                // Set the value
                nextConfig[customKey][functionId][key] = value;

                // Ensure the function object key equals function_identifier
                try {
                    const currentFuncId = functionId;
                    const currentObj = nextConfig[customKey][currentFuncId] || {};
                    const declaredId = currentObj.function_identifier;
                    if (declaredId && declaredId !== currentFuncId) {
                        // Move object under the correct key and delete the old one
                        nextConfig[customKey][declaredId] = { ...currentObj };
                        delete nextConfig[customKey][currentFuncId];
                    }
                } catch (e) {
                    if (process.env.NODE_ENV !== 'production') console.warn('Failed to normalize custom code function key by function_identifier', e);
                }

                if (process.env.NODE_ENV !== 'production') console.log('Custom code structure updated:', nextConfig[customKey]);
            } else if (schemaRootKey) {
                const currentRoot = nextConfig[schemaRootKey];
                if (currentRoot && typeof currentRoot === 'object' && !Array.isArray(currentRoot)) {
                    if (Object.prototype.hasOwnProperty.call(currentRoot, key)) {
                        nextConfig[schemaRootKey] = { ...currentRoot, [key]: value };
                    } else {
                        // Preserve legacy one-child nesting: google_sheets[identifier]
                        const childCandidates = Object.entries(currentRoot).filter(([k, v]) => typeof v === 'object' && v !== null && !Array.isArray(v));
                        if (childCandidates.length === 1) {
                            const [childKey, childObj] = childCandidates[0];
                            nextConfig[schemaRootKey] = { ...currentRoot, [childKey]: { ...(childObj || {}), [key]: value } };
                        } else {
                            nextConfig[schemaRootKey] = { ...currentRoot, [key]: value };
                        }
                    }
                } else {
                    nextConfig[schemaRootKey] = { [key]: value };
                }
            } else {
                // If there is exactly one object key in config, write under it to preserve legacy nested structures
                const keys = Object.keys(nextConfig);
                if (keys.length === 1 && typeof nextConfig[keys[0]] === 'object' && nextConfig[keys[0]] !== null && !Array.isArray(nextConfig[keys[0]])) {
                    const root = keys[0];
                    const rootObj = { ...(nextConfig[root] || {}) };
                    if (Object.prototype.hasOwnProperty.call(rootObj, key)) {
                        // If the key exists at root, overwrite there
                        rootObj[key] = value;
                    } else {
                        // If root has exactly one nested child object, write into it (legacy pattern google_sheets[identifier])
                        const childCandidates = Object.entries(rootObj).filter(([k, v]) => typeof v === 'object' && v !== null && !Array.isArray(v));
                        if (childCandidates.length === 1) {
                            const [childKey, childObj] = childCandidates[0];
                            rootObj[childKey] = { ...(childObj || {}), [key]: value };
                        } else {
                            rootObj[key] = value;
                        }
                    }
                    nextConfig[root] = rootObj;
                } else {
                    nextConfig[key] = value;
                }
            }

            if (process.env.NODE_ENV !== 'production') console.log('Updated config:', nextConfig);
            return { ...prev, config: nextConfig };
        });
    }, [schemaRootKey, selectedTool, getConfigValue]);

    // Render config fields based on normalized schema
    const renderConfigFields = useCallback(() => {
        if (!normalizedSchema) return null;

        const renderField = (key, field) => {
            const fieldType = (field.type || 'text').toLowerCase();
            const label = field.label || key;
            const required = !!field.required;
            const helperText = field.description;
            const options = field.options || [];
            const value = getConfigValue(key, (fieldType === 'multiselect' || fieldType === 'dynamic_fields') ? [] : '');

            // Detect Custom Code tool context
            const isCustomCode = (schemaRootKey === 'custom_code') || ((selectedTool?.name || '').toLowerCase().includes('custom code'));

            // Hide duplicated language field for Custom Code (language is managed by CodeEditor)
            if (isCustomCode && key === 'language') {
                return null;
            }

            // Text and textarea
            if (fieldType === 'text' || fieldType === 'string') {
                return (
                    <TextField
                        key={key}
                        fullWidth
                        margin="normal"
                        label={label}
                        name={key}
                        value={value || ''}
                        onChange={(e) => setConfigValue(key, e.target.value)}
                        required={required}
                        helperText={helperText}
                        variant="outlined"
                    />
                );
            }

            // Password
            if (fieldType === 'password') {
                return (
                    <TextField
                        key={key}
                        fullWidth
                        margin="normal"
                        label={label}
                        name={key}
                        type="password"
                        value={value || ''}
                        onChange={(e) => setConfigValue(key, e.target.value)}
                        required={required}
                        helperText={helperText}
                        variant="outlined"
                        autoComplete="new-password"
                    />
                );
            }

            if (fieldType === 'textarea') {
                // Special UX: for Custom Code tool treat source_code-like fields as code editor
                if (isCustomCode && (key === 'source_code' || key === 'code')) {
                    return (
                        <Box key={key} sx={{ margin: '16px 0' }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                {label} {required && <span style={{ color: 'red' }}>*</span>}
                            </Typography>
                            <CodeEditor
                                value={value || ''}
                                onChange={(newValue) => setConfigValue(key, newValue)}
                                language={getConfigValue('language', 'javascript')}
                                onLanguageChange={(lang) => setConfigValue('language', lang)}
                                customFunctionName={getConfigValue('function_identifier', 'customFunction')}
                            />
                            {helperText && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {helperText}
                                </Typography>
                            )}
                        </Box>
                    );
                }
                return (
                    <TextField
                        key={key}
                        fullWidth
                        margin="normal"
                        label={label}
                        name={key}
                        value={value || ''}
                        onChange={(e) => setConfigValue(key, e.target.value)}
                        required={required}
                        helperText={helperText}
                        multiline
                        rows={4}
                        variant="outlined"
                    />
                );
            }

            // Select / Multiselect
            if (fieldType === 'select' || fieldType === 'multiselect') {
                const normalizedOptions = options.map(opt => (
                    typeof opt === 'string' ? { value: opt, label: opt } : opt
                ));
                const multiple = fieldType === 'multiselect';
                const toPretty = (v) => String(v).replace(/_/g, ' ');

                return (
                    <FormControl key={key} fullWidth margin="normal" required={required} variant="outlined">
                        <InputLabel>{label}</InputLabel>
                        <Select
                            multiple={multiple}
                            value={multiple ? (Array.isArray(value) ? value : []) : (value || '')}
                            label={label}
                            onChange={(e) => setConfigValue(key, multiple ? e.target.value : e.target.value)}
                            renderValue={multiple ? (selected => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected || []).map((val) => (
                                        <Chip
                                            key={val}
                                            label={toPretty(val)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            )) : undefined}
                        >
                            {normalizedOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    <ListItemText primary={opt.label || opt.value} />
                                </MenuItem>
                            ))}
                        </Select>
                        {helperText && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {helperText}
                            </Typography>
                        )}
                    </FormControl>
                );
            }

            // Dynamic fields: list with possible legacy object-map format (Google Sheets) and generic item schema support
            if (fieldType === 'dynamic_fields') {
                // Compact layout for Custom Code parameters
                if (isCustomCode && key === 'parameters') {
                    // Prepare items
                    let items = dynamicDrafts[key];
                    if (!items) {
                        if (Array.isArray(value)) {
                            items = value.map(v => ({ ...v }));
                        } else {
                            items = [];
                        }
                    }

                    const dataTypeOptions = (field?.field_properties?.data_type?.options) || [
                        'string','number','integer','boolean','array','object'
                    ];

                    const commitItems = (arr) => {
                        setDynamicDrafts(prev => ({ ...prev, [key]: arr }));
                        const normalizedArr = (arr || []).map(it => ({ ...it }));
                        setConfigValue(key, normalizedArr);
                    };

                    const handleItemChange = (idx, prop, val) => {
                        const newItems = items.slice();
                        newItems[idx] = { ...(newItems[idx] || {}), [prop]: val };
                        commitItems(newItems);
                    };

                    const addItem = () => {
                        commitItems([...(items || []), { name: '', data_type: 'string', required: false, default: '', description: '' }]);
                    };

                    const removeItem = (idx) => {
                        const newItems = items.slice();
                        newItems.splice(idx, 1);
                        commitItems(newItems);
                    };

                    return (
                        <Box key={key} sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                {label} {required && <span style={{ color: 'red' }}>*</span>}
                            </Typography>
                            {helperText && (
                                <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 2 }}>
                                    {helperText}
                                </Typography>
                            )}
                            {items && items.map((item, idx) => (
                                <Card key={idx} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 500 }}>
                                            Parameter {idx + 1}
                                        </Typography>
                                        <IconButton color="error" size="small" onClick={() => removeItem(idx)}>
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Name"
                                                value={item.name || ''}
                                                onChange={(e) => handleItemChange(idx, 'name', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth size="small" variant="outlined">
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={item.data_type || 'string'}
                                                    label="Type"
                                                    onChange={(e) => handleItemChange(idx, 'data_type', e.target.value)}
                                                >
                                                    {dataTypeOptions.map(type => (
                                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Default Value"
                                                value={item.default || ''}
                                                onChange={(e) => handleItemChange(idx, 'default', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '40px' }}>
                                                <Typography variant="body2">Required:</Typography>
                                                <Switch
                                                    checked={!!item.required}
                                                    onChange={(e) => handleItemChange(idx, 'required', e.target.checked)}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Description"
                                                multiline
                                                rows={2}
                                                value={item.description || ''}
                                                onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            ))}
                            <Button
                                startIcon={<Add />}
                                onClick={addItem}
                                variant="outlined"
                                size="small"
                                color="primary"
                            >
                                Add Parameter
                            </Button>
                        </Box>
                    );
                }

                // Google Sheets-like dynamic fields (Object Map format)
                let items = dynamicDrafts[key];
                if (!items) {
                    if (value && Array.isArray(value)) {
                        items = value.slice();
                    } else if (value && typeof value === 'object') {
                        items = Object.entries(value).map(([k, v]) => ({ key: k, value: v }));
                    } else {
                        items = [];
                    }
                }

                const isObjectMapFormat = field?.format === 'object_map';

                const commitItems = (arr) => {
                    setDynamicDrafts(prev => ({ ...prev, [key]: arr }));
                    if (isObjectMapFormat) {
                        const map = {};
                        arr.forEach(item => {
                            if (item.key) map[item.key] = item.value;
                        });
                        setConfigValue(key, map);
                    } else {
                        setConfigValue(key, arr);
                    }
                };

                const handleItemChange = (idx, prop, val) => {
                    const newItems = items.slice();
                    newItems[idx] = { ...(newItems[idx] || {}), [prop]: val };
                    commitItems(newItems);
                };

                const addItem = () => {
                    commitItems([...(items || []), { key: '', value: '' }]);
                };

                const removeItem = (idx) => {
                    const newItems = items.slice();
                    newItems.splice(idx, 1);
                    commitItems(newItems);
                };

                return (
                    <Box key={key} sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                            {label} {required && <span style={{ color: 'red' }}>*</span>}
                        </Typography>
                        {helperText && (
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 2 }}>
                                {helperText}
                            </Typography>
                        )}
                        {items && items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    label="Key"
                                    value={item.key || ''}
                                    onChange={(e) => handleItemChange(idx, 'key', e.target.value)}
                                    sx={{ flex: 1 }}
                                    variant="outlined"
                                />
                                <TextField
                                    size="small"
                                    label="Value"
                                    value={item.value || ''}
                                    onChange={(e) => handleItemChange(idx, 'value', e.target.value)}
                                    sx={{ flex: 1 }}
                                    variant="outlined"
                                />
                                <IconButton color="error" size="small" onClick={() => removeItem(idx)}>
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<Add />}
                            onClick={addItem}
                            variant="outlined"
                            size="small"
                            color="primary"
                        >
                            Add Field
                        </Button>
                    </Box>
                );
            }

            return null;
        };

        return Object.entries(normalizedSchema).map(([key, field]) => renderField(key, field));
    }, [normalizedSchema, schemaRootKey, selectedTool, getConfigValue, setConfigValue, dynamicDrafts]);

    if (loadingPage) {
        return (
            <DashboardLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" component="h1">
                        Integrations
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleDialogOpen}
                        size="large"
                    >
                        Add Integration
                    </Button>
                </Box>

                {/* Search */}
                <Paper sx={{ p: 2, mb: 3, elevation: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Search integrations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                    />
                </Paper>

                {/* Tabs */}
                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label="All" />
                    <Tab label="Active" />
                    <Tab label="Inactive" />
                </Tabs>

                {/* Integrations list */}
                <Grid container spacing={3}>
                    {filteredIntegrations.map((integration) => (
                        <Grid item xs={12} md={6} lg={4} key={integration.id}>
                            <IntegrationCard
                                integration={integration}
                                onEdit={handleEditDialogOpen}
                                onDelete={handleDeleteDialogOpen}
                            />
                        </Grid>
                    ))}
                </Grid>

                {filteredIntegrations.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <ExtensionIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h5" color="text.secondary" gutterBottom>
                            {searchQuery ? 'No integrations found' : 'You have no integrations yet'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {searchQuery ? 'Try adjusting your search criteria' : 'Create your first integration to get started'}
                        </Typography>
                        {!searchQuery && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleDialogOpen}
                                size="large"
                            >
                                Create Integration
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    elevation: 8
                }}
            >
                <form onSubmit={handleFormSubmit}>
                    <DialogTitle sx={{ pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {dialogMode === 'create' ? <AddIcon /> : <IntegrationIcon />}
                            <Typography variant="h6">
                                {dialogMode === 'create' ? 'Create Integration' : 'Edit Integration'}
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pt: '8px !important' }}>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="normal"
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            variant="outlined"
                        />

                        {dialogMode === 'create' ? (
                            <FormControl fullWidth margin="normal" required variant="outlined">
                                <InputLabel>Tool</InputLabel>
                                <Select
                                    label="Tool"
                                    name="tool_id"
                                    value={formData.tool_id}
                                    onChange={handleInputChange}
                                >
                                    {tools.map((tool) => (
                                        <MenuItem key={tool.id} value={tool.id}>
                                            <ListItemIcon>
                                                {tool.icon ? (
                                                    <Avatar
                                                        src={tool.icon}
                                                        sx={{ width: 24, height: 24 }}
                                                    />
                                                ) : (
                                                    <IntegrationIcon />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText primary={tool.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Box sx={{ mt: 2, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Tool
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'grey.50'
                                }}>
                                    {selectedTool?.icon ? (
                                        <Avatar
                                            src={selectedTool.icon}
                                            sx={{ width: 24, height: 24 }}
                                        />
                                    ) : (
                                        <IntegrationIcon />
                                    )}
                                    <Typography variant="body1">
                                        {selectedTool?.name}
                                    </Typography>
                                    <LockIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 'auto' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    Tool cannot be changed for existing integrations
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            multiline
                            rows={3}
                            variant="outlined"
                        />

                        {selectedTool && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {selectedTool.icon ? (
                                        <Avatar
                                            src={selectedTool.icon}
                                            sx={{ width: 24, height: 24 }}
                                        />
                                    ) : (
                                        <IntegrationIcon />
                                    )}
                                    {selectedTool.name} Configuration
                                </Typography>
                                {renderConfigFields()}
                            </>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button onClick={handleDialogClose} size="large">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loadingDialog}
                            size="large"
                        >
                            {loadingDialog && <CircularProgress size={20} sx={{ mr: 1 }} />}
                            {dialogMode === 'create' ? 'Create' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={handleDeleteDialogClose}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete integration "{selectedIntegration?.name}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteIntegration}
                        color="error"
                        disabled={loadingAction}
                    >
                        {loadingAction && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default IntegrationsPage;